import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackRatingSubmitted, trackRatingRemindLater } from "@/lib/mixpanelRatingEvents";

interface RateAppDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function RateAppDialog({ open, onClose }: RateAppDialogProps) {
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Save rating to database
        await supabase.from("app_ratings").insert({
          user_id: user.id,
          rating,
        });
      }

      // Mark as completed so it never shows again
      localStorage.setItem("rateAppCompleted", "true");
      localStorage.removeItem("rateAppRemindAt");
      
      // Track in Mixpanel
      trackRatingSubmitted(rating);
      
      toast.success("Thank you for your rating!");
      onClose();
    } catch (error) {
      console.error("Failed to submit rating:", error);
      toast.error("Failed to submit rating");
    }
  };

  const handleLater = () => {
    // Store reminder to ask again in 7 days
    const remindAt = new Date();
    remindAt.setDate(remindAt.getDate() + 7);
    localStorage.setItem("rateAppRemindAt", remindAt.toISOString());
    
    // Track in Mixpanel
    trackRatingRemindLater();
    
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Enjoying FaceFable™?</DialogTitle>
          <DialogDescription className="text-center">
            Your feedback helps us improve. Please rate your experience!
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center gap-2 py-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              className="transition-transform hover:scale-110 focus:outline-none"
            >
              <Star
                className={`h-10 w-10 transition-colors ${
                  star <= (hoveredStar || rating)
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-muted-foreground"
                }`}
              />
            </button>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button onClick={handleSubmit} disabled={rating === 0} className="w-full">
            Submit Rating
          </Button>
          <Button onClick={handleLater} variant="ghost" className="w-full">
            Remind Me Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
